// Client-side OCR using tesseract.js. The library and the language data are
// loaded lazily on first use so the main bundle stays lean. Workers are
// cached per language so the same recognize call doesn't redownload data.

const workerByLang = new Map()

async function getWorker(lang, onProgress) {
  if (workerByLang.has(lang)) return workerByLang.get(lang)
  const promise = (async () => {
    const Tesseract = await import('tesseract.js')
    const worker = await Tesseract.createWorker(lang, 1, {
      logger: (m) => onProgress && onProgress(m),
    })
    return worker
  })()
  workerByLang.set(lang, promise)
  return promise
}

export async function disposeOcr() {
  for (const [, p] of workerByLang) {
    try {
      const w = await p
      await w.terminate()
    } catch (_) {
      // ignore
    }
  }
  workerByLang.clear()
}

export const OCR_LANGUAGES = [
  { code: 'eng', label: 'English' },
  { code: 'spa', label: 'Spanish' },
  { code: 'fra', label: 'French' },
  { code: 'deu', label: 'German' },
  { code: 'ita', label: 'Italian' },
  { code: 'por', label: 'Portuguese' },
  { code: 'nld', label: 'Dutch' },
  { code: 'rus', label: 'Russian' },
  { code: 'jpn', label: 'Japanese' },
  { code: 'kor', label: 'Korean' },
  { code: 'chi_sim', label: 'Chinese (simplified)' },
  { code: 'chi_tra', label: 'Chinese (traditional)' },
  { code: 'ara', label: 'Arabic' },
  { code: 'hin', label: 'Hindi' },
  { code: 'tur', label: 'Turkish' },
]

export async function recognizePage(
  source,
  { sourceWidth, sourceHeight, language = 'eng', onProgress } = {}
) {
  const worker = await getWorker(language, onProgress)
  const { data } = await worker.recognize(source)

  const w = sourceWidth || data.imageWidth || 1
  const h = sourceHeight || data.imageHeight || 1

  const words = []
  const seen = new Set()
  for (const word of data.words || []) {
    const text = (word.text || '').trim()
    if (!text) continue
    const bbox = word.bbox
    if (!bbox) continue
    const xRatio = bbox.x0 / w
    const yRatio = bbox.y0 / h
    const wRatio = Math.max(0, (bbox.x1 - bbox.x0) / w)
    const hRatio = Math.max(0, (bbox.y1 - bbox.y0) / h)
    if (wRatio <= 0 || hRatio <= 0) continue
    const key = `${text}|${xRatio.toFixed(4)}|${yRatio.toFixed(4)}`
    if (seen.has(key)) continue
    seen.add(key)
    words.push({
      text,
      xRatio,
      yRatio,
      wRatio,
      hRatio,
      confidence: word.confidence ?? 0,
    })
  }

  return {
    words,
    fullText: data.text || '',
    confidence: data.confidence ?? 0,
  }
}
